import { streamerDashboardSchema, type StreamerDashboard } from '@growup/streaming-shared';
import { Card, Notice, Select, Table, type Column } from '@growup/ui';
import { useEffect, useState } from 'react';

import { getJson, messageOf } from '../api';
import { formatDateTime } from '../format';
import { formatCount, formatMinutes } from './labels';

/**
 * O painel de um streamer (numeros da Twitch, `streaming.manage`): na seccao
 * Streaming e na ficha de socio.
 */
export function StreamerDashboardCard({ streamerId }: { streamerId: string }) {
  const [days, setDays] = useState(30);
  const [dashboard, setDashboard] = useState<StreamerDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getJson(
      `/api/streaming/streamers/${streamerId}/dashboard?days=${String(days)}`,
      streamerDashboardSchema,
    )
      .then((d) => {
        setError(null);
        setDashboard(d);
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, [streamerId, days]);

  return (
    <Card
      headingLevel={2}
      title="Números da Twitch"
      actions={
        <Select
          aria-label="Período"
          value={String(days)}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          <option value="7">7 dias</option>
          <option value="30">30 dias</option>
          <option value="90">90 dias</option>
        </Select>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {dashboard ? (
        <DashboardBody data={dashboard} />
      ) : error ? null : (
        <p className="text-gu-ink-muted">A carregar…</p>
      )}
    </Card>
  );
}

const SESSION_COLUMNS: Column<StreamerDashboard['sessions'][number]>[] = [
  { key: 'when', header: 'Início', cell: (x) => formatDateTime(x.startedAt) },
  {
    key: 'what',
    header: 'Transmissão',
    cell: (x) => [x.title, x.game].filter(Boolean).join(' · ') || '—',
  },
  {
    key: 'time',
    header: 'Duração',
    align: 'right',
    cell: (x) => (x.endedAt === null ? 'no ar' : formatMinutes(x.minutes)),
  },
  {
    key: 'viewers',
    header: 'Média · pico',
    align: 'right',
    cell: (x) => `${formatCount(x.avgViewers)} · ${formatCount(x.peakViewers)}`,
  },
];

function DashboardBody({ data }: { data: StreamerDashboard }) {
  const t = data.totals;
  return (
    <div className="flex flex-col gap-4">
      {data.syncedAt === null ? (
        <Notice tone="info">
          Ainda sem leituras da Twitch. Com a Twitch configurada, a primeira chega em minutos.
        </Notice>
      ) : (
        <p className="text-caption text-gu-ink-muted">
          Última leitura {formatDateTime(data.syncedAt)}
        </p>
      )}
      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <Stat
          label="Em direto"
          value={`${formatMinutes(t.minutesLive)} · ${String(t.sessions)}×`}
        />
        <Stat label="Espectadores (média)" value={formatCount(t.avgViewers)} />
        <Stat label="Pico" value={formatCount(t.peakViewers)} />
        <Stat
          label="Seguidores"
          value={
            t.followers === null
              ? '—'
              : `${formatCount(t.followers)}${t.followersChange ? ` (${t.followersChange > 0 ? '+' : ''}${formatCount(t.followersChange)})` : ''}`
          }
        />
      </dl>
      <Table
        caption="Transmissões"
        columns={SESSION_COLUMNS}
        rows={data.sessions}
        rowKey={(x) => x.startedAt}
        empty="Sem transmissões neste período."
      />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1 border-l-2 border-gu-accent pl-3">
      <dt className="text-caption text-gu-ink-muted">{label}</dt>
      <dd className="font-display text-title">{value}</dd>
    </div>
  );
}
